import { Component, inject, OnInit } from '@angular/core';
import { AppInformationService, BrewersUnitOfWork } from 'app/core/core.module';
import { Customer } from 'app/core/model/customer';
import { CustomerMixStrengthLink } from 'app/core/model/customer-mix-strength-link';
import { Product } from 'app/core/model/product';
import { ProductCustomerTypeLink } from 'app/core/model/product-customer-type-link';
import DataSource from 'devextreme/data/data_source';
import validationEngine from 'devextreme/ui/validation_engine';
import moment from 'moment';
import { Subject } from 'rxjs';

export type ManualLoadModel = {
    customerId: number;
    customerString: string;
    productId: number;
    productString: string;
    job: string;
    truck: string;
    ticketNumber: number;
    loadCount: number;
    weightPerLoad: number;
    price: number;
    loadDate: Date;
    description: string;
    hasDelivery: boolean;
    deliveryProductId: number;
    deliveryPrice: number;
    isHourly: boolean;
    startTime: Date;
    endTime: Date;
};

@Component({
    standalone: false,
    selector: 'app-manual-load-dialog',
    templateUrl: './manual-load-dialog.component.html',
    styleUrl: './manual-load-dialog.component.scss',
})
export class ManualLoadDialogComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    appInfo = inject(AppInformationService);

    result = new Subject<{ success: boolean; value?: ManualLoadModel }>();
    model: ManualLoadModel;
    customersDS: DataSource;
    productsDS: DataSource;
    customers: Customer[];
    products: Product[];
    product: Product;
    customerTypeLink: ProductCustomerTypeLink;
    mixStrengthLink: CustomerMixStrengthLink;
    isEditing: boolean;
    deliveryProducts: Product[];

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        this.isEditing = !!this.model;
        if (!this.isEditing)
            this.model = {
                ticketNumber: null,
                loadCount: null,
                weightPerLoad: null,
                price: null,
                loadDate: moment().startOf('day').toDate(),
                hasDelivery: false,
                deliveryPrice: 0,
                startTime: moment(new Date()).startOf('day').toDate(),
                endTime: moment(new Date()).startOf('day').toDate(),
            } as ManualLoadModel;

        this.customers = await this.uow.customers.whereActive('customerUnixKey');
        this.products = await this.uow.getProducts(null, 'priceUom');
        this.customersDS = new DataSource({ store: this.customers, paginate: true });
        this.productsDS = new DataSource({ store: this.products, paginate: true });

        this.deliveryProducts = await this.uow.getDeliveryProducts(1);
        this.appInfo.pleaseWaitHide();

        this.setProduct(this.model.productId, false);
        this.getMixStrengthLink();
        this.getCustomerTypeLink();
    }

    productChanged = (e: any) => {
        const productId = e.value;
        this.setProduct(productId);
        this.getMixStrengthLink();
        this.getCustomerTypeLink();
    };

    deliveryCheckBoxChanged = (e: any) => {
        if (e.value) {
            this.model.deliveryProductId = this.product?.deliveryProductId;
            this.model.deliveryPrice = this.product?.deliveryProduct?.basePrice ?? 0;
        } else {
            this.model.deliveryProductId = null;
            this.model.deliveryPrice = 0;
        }
    };

    setProduct(productId?: number, autoPopulateDelivery: boolean = true) {
        this.product = !productId ? null : this.products.find((p) => p.id === productId);
        if (this.product?.priceUom.name.toUpperCase() == 'HOUR') {
            this.model.isHourly = true;
        }
        this.model.productString = this.product?.partNumber;
        if (autoPopulateDelivery && this.model.hasDelivery) {
            this.model.deliveryProductId = this.product?.deliveryProductId;
            this.model.deliveryPrice = this.product?.deliveryProduct?.basePrice ?? 0;
        }
    }

    customerChanged = (e: any) => {
        const customerId = e.value;
        this.model.customerString = !customerId ? null : this.customers.find((p) => p.id === customerId)?.customerUnixKey;
        this.getMixStrengthLink();
        this.getCustomerTypeLink();
    };

    async getMixStrengthLink() {
        this.mixStrengthLink = null;
        if (!this.model.productId || !this.model.customerId) return;

        const links = await this.uow.getCustomerMixStrengthLinks(this.model.customerId, this.model.productId);
        this.mixStrengthLink = links[0];
    }

    async getCustomerTypeLink() {
        this.customerTypeLink = null;
        if (!this.model.productId || !this.model.customerId) return;

        const customer = this.customers.find((p) => p.id === this.model.customerId);
        const links = await this.uow.getLatestCustomerTypePrices(this.model.productId);
        this.customerTypeLink = links.find((link) => link.customerTypeId === customer.typeId);
    }

    save() {
        const success = this.valid();
        if (!success) return;
        this.result.next({ success: true, value: this.model });
    }

    cancel() {
        this.result.next({ success: false });
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('manualLoadValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('manualLoadValidationGroup');
            return result.isValid;
        } else return false;
    }
}
